import { FC } from 'react'
import mainStyles from 'containers/authorised/setting/v2/payroll/PaymentProcess/payment.module.css'
import classNames from 'classnames'
import { Flex, Badge, Button } from '@jisr-hr/design-system'
import { Typography } from '@jisr-hr/ds'
import { config, useTransition, animated } from 'react-spring'
import Avatar from 'components/global/templates/RequestDrawer/ds/Avatar'
import i18n from 'translations/i18n'
import { BankAccountType } from 'redux/setting/payrollManagement/paymentMethods/reducer'
import { paymentMethodChangeDefaultBank } from 'redux/setting/payrollManagement/paymentMethods/actionCreators'
import { useDispatch, useSelector } from 'utils/hooks'
import DSIcon from 'components/DSIcon'

import { usePaymentMethod } from '../../components/usePaymentMethods'

const PaymentAccountsList: FC = () => {
  const dispatch = useDispatch()
  const paymentCtx = usePaymentMethod()
  const { selectedPaymentMethod } = paymentCtx

  const loading = useSelector((s) => s.paymentMethods.loading)

  const animatedBanksList = useTransition(selectedPaymentMethod?.bank_accounts || [], {
    keys: (item) => Number(item?.id),
    from: { opacity: 0, transform: 'translate3d(0, 10px, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    config: config.wobbly,
    trail: 250,
  })

  // ? functions
  const handleAccountDel = (account_id: number): void => {
    if (!selectedPaymentMethod) return
    paymentCtx.handleBankAccountDel({
      account_id,
      onSuccess: () => {
        const bank_accounts = selectedPaymentMethod.bank_accounts.filter(
          (account) => account.id !== account_id,
        )
        paymentCtx.setSelectedPaymentMethod({ ...selectedPaymentMethod, bank_accounts })
      },
    })
  }

  const handleSetBankDefault = async (account: BankAccountType): Promise<void> => {
    if (!selectedPaymentMethod) return
    const { id, active_payment_method, bank_accounts } = selectedPaymentMethod
    await dispatch(
      paymentMethodChangeDefaultBank(id, {
        bank_account_id: account.id,
        payment_mode: active_payment_method ?? 'offline',
      }),
    )

    paymentCtx.setSelectedPaymentMethod({
      ...selectedPaymentMethod,
      bank_accounts: bank_accounts.map((account_bank) => ({
        ...account_bank,
        is_active: account_bank.id === account.id,
      })),
    })
  }

  return (
    <div>
      {animatedBanksList((style, account) => {
        return (
          <animated.div
            key={account.id}
            className={classNames(mainStyles.main_widget)}
            style={{ ...style, marginBottom: 16 }}
          >
            <Flex
              itemCenter
              justifyBetween
              style={{ marginBottom: 12 }}
            >
              <Avatar
                src={account.bank_logo}
                name={account.bank_name}
                className={mainStyles.account_logo}
              />
              {account.authorization_status === 'authorized' && !account.is_active && (
                <Button
                  label={i18n.t('set_as_default')}
                  size="medium"
                  disabled={loading}
                  onClick={(): Promise<void> => handleSetBankDefault(account)}
                  variant="secondary"
                />
              )}
              {account.authorization_status === 'authorized' && account.is_active && (
                <Flex
                  itemsCenter
                  style={{ gap: 12 }}
                >
                  <DSIcon
                    size="lg"
                    color="var(--color-brand-primary-default-new)"
                    name="check"
                  />
                  <Typography
                    variant="interface/default/sm"
                    style={{ color: 'var(--color-base-colors-blue-700)' }}
                    text={i18n.t('default')}
                  />
                </Flex>
              )}
              {!account.authorization_status && (
                <DSIcon
                  name="trash-01"
                  size="ml"
                  color="var(--ds-core-color-gray-400)"
                  className={mainStyles.deleteIcon}
                  onClick={(): void => handleAccountDel(account.id)}
                />
              )}
            </Flex>
            <Typography
              variant="interface/default/sm"
              className={mainStyles.secondary_text}
              text={i18n.t('bank_account_name_iban_format', {
                name: account.bank_name,
                iban: account.iban.replace('SA', ''),
              })}
            />
            <div className={mainStyles.payment_info_divider}>
              <Typography
                variant="interface/default/sm"
                className={mainStyles.primary_text}
                text={`${i18n.t('commercial_registration')} : ${
                  selectedPaymentMethod?.registration_number
                }`}
              />
              <Typography
                variant="interface/default/sm"
                className={mainStyles.primary_text}
                text={`${i18n.t('mol_registration_number')} :${' '}
                ${selectedPaymentMethod?.mol_registration.mol_number}`}
              />
            </div>
            {account.authorization_status && (
              <Badge
                label={i18n.t(account.authorization_status)}
                type={account.authorization_status === 'authorized' ? 'success' : 'error'}
                size="small"
                variant="stroke"
                style={{ borderRadius: 16, marginTop: 16 }}
              />
            )}
          </animated.div>
        )
      })}
    </div>
  )
}

export default PaymentAccountsList
